import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { Modal } from 'react-bootstrap'
import { KTCard, KTCardBody } from '../../../../_metronic/helpers'
import { KTIcon } from '../../../../_metronic/helpers'
import { EditSubcategory } from './EditSubcategory'
import { 
  get_all_subcategories,
  delete_subcategory,
  update_subcategory
} from '../api/subcategoriesApi'
import { get_all_categories } from '../../projects/api/projectCategoriesApi'
import { toast } from 'react-toastify'
import { LoadingProgressBar } from '../../projects/components/LoadingProgressBar'
import { permissionCheck } from '../../../helper-functions/PermissionCheck'

// Subcategory type for this module
interface SubcategoryItem {
  id: number
  subcategory_name: string
  category_id: number
  category_name?: string
  description: string
  created_at: string
  updated_at: string
  status: number
}

interface CategoryItem {
  id: number
  category_name: string
}

const SubcategoriesList: React.FC = () => {
  const navigate = useNavigate()
  const [subcategories, setSubcategories] = useState<SubcategoryItem[]>([])
  const [filteredSubcategories, setFilteredSubcategories] = useState<SubcategoryItem[]>([])
  const [categories, setCategories] = useState<CategoryItem[]>([])
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedStatus, setSelectedStatus] = useState<string>('')
  const [selectedCategory, setSelectedCategory] = useState<string>('')
  const [isLoading, setIsLoading] = useState(false)
  const [showEditModal, setShowEditModal] = useState(false)
  const [selectedSubcategory, setSelectedSubcategory] = useState<SubcategoryItem | null>(null)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [subcategoryToDelete, setSubcategoryToDelete] = useState<SubcategoryItem | null>(null)
  const [currentPage, setCurrentPage] = useState(1)
  const [pageSize, setPageSize] = useState(10)
  const canViewSubcategories = Boolean(permissionCheck('subcategory-view'))
  const canCreateSubcategory = Boolean(permissionCheck('subcategory-create'))
  const canUpdateSubcategory = Boolean(permissionCheck('subcategory-update'))
  const canDeleteSubcategory = Boolean(permissionCheck('subcategory-delete'))

  // Load categories first, then subcategories
  useEffect(() => {
    const loadData = async () => {
      try {
        setIsLoading(true)
        if (canViewSubcategories) {
          // Load categories first so we can map category names
          await fetchAllCategories()
          // Then load subcategories
          await fetchAllSubcategories()
        }
      } catch (error) {
        console.error('Error loading subcategories:', error)
        toast.error('Failed to load subcategories')
      } finally {
        setIsLoading(false)
      }
    }
    if (canViewSubcategories) {
      loadData()
    }
  }, [canViewSubcategories])

  const fetchAllCategories = async () => {
    try {
      const res = await get_all_categories()
      const apiCats = res?.data?.all_categories || res?.data?.categories || res?.data || []
      const all: CategoryItem[] = (Array.isArray(apiCats) ? apiCats : []).map((c: any) => ({
        id: Number(c.id),
        category_name: c.category_name
      }))
      setCategories(all)
    } catch (error) {
      console.error('Error fetching categories:', error)
    }
  }

  const fetchAllSubcategories = async () => {
    try {
      const res = await get_all_subcategories()
      // API returns data in "all_categories" key (even though it's subcategories data)
      const apiSubcats = res?.data?.all_categories || res?.data?.all_subcategories || res?.data?.subcategories || res?.data || []
      const all: SubcategoryItem[] = (Array.isArray(apiSubcats) ? apiSubcats : []).map((sc: any) => {
        // Get category name from API response (category_name field or category.category_name)
        const categoryName = sc.category_name || sc.category?.category_name || ''
        // Fallback: Find category name from categories list if not in response
        const category = !categoryName ? categories.find(cat => cat.id === Number(sc.category_id)) : null
        return {
          id: Number(sc.id),
          subcategory_name: sc.subcategory_name,
          category_id: Number(sc.category_id),
          category_name: categoryName || category?.category_name || '',
          description: sc.description || '',
          created_at: sc.created_at,
          updated_at: sc.updated_at,
          status: Number(sc.status)
        }
      })
      setSubcategories(all)
      setFilteredSubcategories(all)
    } catch (error) {
      console.error('Error fetching subcategories:', error)
      toast.error('Failed to load subcategories')
      setSubcategories([])
      setFilteredSubcategories([])
    }
  }

  // Filter subcategories based on search term, status, and category
  useEffect(() => {
    let filtered = subcategories

    if (searchTerm) {
      filtered = filtered.filter(subcategory =>
        subcategory.subcategory_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        subcategory.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
        subcategory.category_name?.toLowerCase().includes(searchTerm.toLowerCase())
      )
    }

    if (selectedStatus !== '') {
      filtered = filtered.filter(subcategory => String(subcategory.status) === selectedStatus)
    }

    if (selectedCategory !== '') {
      filtered = filtered.filter(subcategory => String(subcategory.category_id) === selectedCategory)
    }

    setFilteredSubcategories(filtered)
  }, [subcategories, searchTerm, selectedStatus, selectedCategory])

  // Pagination derived values
  const totalPages = Math.max(1, Math.ceil(filteredSubcategories.length / pageSize))
  const pageSafe = Math.min(currentPage, totalPages)
  const startIdx = (pageSafe - 1) * pageSize
  const endIdx = startIdx + pageSize
  const paginatedSubcategories = filteredSubcategories.slice(startIdx, endIdx)

  useEffect(() => {
    setCurrentPage(1)
  }, [searchTerm, selectedStatus, selectedCategory, pageSize])


  const handleEditSubcategory = async (updatedSubcategory: SubcategoryItem) => {
    if (!canUpdateSubcategory) {
      toast.error('You do not have permission to update subcategories')
      return
    }
    try {
      const payload = {
        subcategory_name: updatedSubcategory.subcategory_name,
        category_id: updatedSubcategory.category_id,
        description: updatedSubcategory.description,
        status: String(updatedSubcategory.status)
      }
      await update_subcategory(String(updatedSubcategory.id), payload)
      toast.success('Subcategory updated')
      await fetchAllSubcategories()
      setShowEditModal(false)
      setSelectedSubcategory(null)
    } catch (error: any) {
      console.error('Error updating subcategory:', error)
      const errorMessage = error?.response?.data?.message || error?.message || 'Failed to update subcategory'
      toast.error(errorMessage)
    }
  }

  const handleDeleteClick = (subcategory: SubcategoryItem) => {
    if (!canDeleteSubcategory) {
      toast.error('You do not have permission to delete subcategories')
      return
    }
    setSubcategoryToDelete(subcategory)
    setShowDeleteModal(true)
  }

  const handleDeleteConfirm = async () => {
    if (!canDeleteSubcategory) {
      toast.error('You do not have permission to delete subcategories')
      return
    }
    if (!subcategoryToDelete) return
    try {
      await delete_subcategory(String(subcategoryToDelete.id))
      toast.success('Subcategory deleted')
      await fetchAllSubcategories()
    } catch (error: any) {
      console.error('Error deleting subcategory:', error)
      const errorMessage = error?.response?.data?.message || error?.message || 'Failed to delete subcategory'
      toast.error(errorMessage)
    } finally {
      setShowDeleteModal(false)
      setSubcategoryToDelete(null)
    }
  }

  const handleEditClick = (subcategory: SubcategoryItem) => {
    if (!canUpdateSubcategory) {
      toast.error('You do not have permission to update subcategories')
      return
    }
    setSelectedSubcategory(subcategory)
    setShowEditModal(true)
  }

  if (!canViewSubcategories) {
    return (
      <KTCard>
        <KTCardBody>
          <div className='alert alert-warning mb-0'>
            You do not have permission to view subcategories.
          </div>
        </KTCardBody>
      </KTCard>
    )
  }

  return (
    <>
      <LoadingProgressBar isLoading={isLoading} />
      <KTCard>
        <KTCardBody>
          <div className='d-flex flex-column flex-lg-row'>
            <div className='flex-lg-row-fluid mb-10 mb-lg-0 me-lg-7'>
              <h3 className='fw-bold text-dark'>Subcategories Management</h3>
              <p className='text-muted fs-6'>Manage your subcategories</p>
            </div>
            {canCreateSubcategory && (
              <div className='d-flex align-items-center'>
                <button
                  className='btn btn-primary'
                  onClick={() => navigate('/subcategories/add')}
                >
                  <KTIcon iconName='plus' className='fs-2' />
                  Add Subcategory
                </button>
              </div>
            )}
          </div>
        </KTCardBody>
      </KTCard>

      <KTCard>
        <KTCardBody>
          {/* Search and Filter Section */}
          <div className='d-flex align-items-center gap-3 flex-wrap mb-6'>
            <div className='position-relative' style={{minWidth: 280}}>
              <KTIcon
                iconName='magnifier'
                className='fs-3 position-absolute top-50 translate-middle-y ms-3'
              />
              <input
                type='text'
                className='form-control form-control-solid ps-13'
                placeholder='Search subcategories...'
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                style={{minWidth: 280}}
              />
            </div>
            <div style={{minWidth: 160}}>
              <select
                className='form-select form-select-solid'
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
              >
                <option value=''>All Categories</option>
                {categories.map(cat => (
                  <option key={cat.id} value={cat.id}>{cat.category_name}</option>
                ))}
              </select>
            </div>
            <div style={{minWidth: 160}}>
              <select
                className='form-select form-select-solid'
                value={selectedStatus}
                onChange={(e) => setSelectedStatus(e.target.value)}
              >
                <option value=''>All Status</option>
                <option value='1'>Active</option>
                <option value='0'>Inactive</option>
              </select>
            </div>
            <div className='ms-auto'>
              <button
                className='btn btn-light-primary'
                onClick={() => {
                  setSearchTerm('')
                  setSelectedStatus('')
                  setSelectedCategory('')
                }}
              >
                Clear Filters
              </button>
            </div>
          </div>

          {/* Subcategories Table */}
          <div className='table-responsive'>
            <table className='table table-row-dashed table-row-gray-300 align-middle gs-0 gy-4'>
              <thead>
                <tr className='fw-bold text-muted'>
                  <th className='min-w-150px'>Name</th>
                  <th className='min-w-150px'>Category</th>
                  <th className='min-w-200px'>Description</th>
                  <th className='min-w-80px'>Status</th>
                  <th className='min-w-100px'>Updated At</th>
                  <th className='min-w-100px text-end'>Actions</th>
                </tr>
              </thead>
              <tbody>
                {isLoading ? (
                  Array.from({length: 6}).map((_, idx) => (
                    <tr key={`sk-${idx}`}>
                      <td>
                        <div className='placeholder-glow'>
                          <span className='placeholder col-8'></span>
                        </div>
                      </td>
                      <td>
                        <div className='placeholder-glow'>
                          <span className='placeholder col-8'></span>
                        </div>
                      </td>
                      <td>
                        <div className='placeholder-glow'>
                          <span className='placeholder col-10'></span>
                        </div>
                      </td>
                      <td>
                        <div className='placeholder-glow'>
                          <span className='placeholder col-6'></span>
                        </div>
                      </td>
                      <td>
                        <div className='placeholder-glow'>
                          <span className='placeholder col-4'></span>
                        </div>
                      </td>
                      <td className='text-end'>
                        <div className='d-flex justify-content-end flex-shrink-0 placeholder-glow'>
                          <span className='placeholder btn btn-icon btn-bg-light btn-sm me-1 col-2'></span>
                          <span className='placeholder btn btn-icon btn-bg-light btn-sm col-2'></span>
                        </div>
                      </td>
                    </tr>
                  ))
                ) : filteredSubcategories.length === 0 ? (
                  <tr>
                    <td colSpan={6} className='text-center py-10'>
                      <div className='text-muted'>No subcategories found</div>
                    </td>
                  </tr>
                ) : (
                  paginatedSubcategories.map((subcategory) => (
                    <tr key={subcategory.id}>
                      <td>
                        <div className='d-flex align-items-center'>
                          <div className='d-flex justify-content-start flex-column'>
                            <span className='text-dark fw-bold text-hover-primary fs-6'>
                              {subcategory.subcategory_name}
                            </span>
                          </div>
                        </div>
                      </td>
                      <td>
                        <span className='text-muted fw-bold text-muted d-block fs-7'>
                          {subcategory.category_name || 'N/A'}
                        </span>
                      </td>
                      <td>
                        <span 
                          className='text-muted fw-bold text-muted d-block fs-7'
                          style={{
                            maxWidth: '250px',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                          }}
                          title={subcategory.description || 'N/A'}
                        >
                          {subcategory.description || 'N/A'}
                        </span>
                      </td>
                      <td>
                        <span className={`badge ${subcategory.status === 1 ? 'badge-light-success' : 'badge-light-danger'} fw-bold`}>
                          {subcategory.status === 1 ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td>
                        <span className='text-muted fw-bold text-muted d-block fs-7'>
                          {subcategory.updated_at ? new Date(subcategory.updated_at).toLocaleDateString() : 'N/A'}
                        </span>
                      </td>
                      <td className='text-end'>
                        <div className='d-flex justify-content-end flex-shrink-0'>
                          {canUpdateSubcategory && (
                            <button
                              className='btn btn-icon btn-bg-light btn-active-color-primary btn-sm me-1'
                              onClick={() => handleEditClick(subcategory)}
                            >
                              <KTIcon iconName='pencil' className='fs-3' />
                            </button>
                          )}
                          {canDeleteSubcategory && (
                            <button
                              className='btn btn-icon btn-bg-light btn-active-color-danger btn-sm'
                              onClick={() => handleDeleteClick(subcategory)}
                            >
                              <KTIcon iconName='trash' className='fs-3' />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination controls */}
          <div className='d-flex align-items-center justify-content-between mt-4'>
            <div className='d-flex align-items-center gap-3'>
              <span className='text-muted fs-7'>
                Showing {filteredSubcategories.length === 0 ? 0 : startIdx + 1}-{Math.min(endIdx, filteredSubcategories.length)} of {filteredSubcategories.length}
              </span>
              <div className='d-flex align-items-center'>
                <span className='me-2 text-muted fs-7'>Show</span>
                <select
                  className='form-select form-select-sm w-auto'
                  value={pageSize}
                  onChange={(e) => setPageSize(Number(e.target.value))}
                >
                  <option value={10}>10</option>
                  <option value={20}>20</option>
                  <option value={50}>50</option>
                </select>
                <span className='ms-2 text-muted fs-7'>per page</span>
              </div>
            </div>
            <ul className='pagination'>
              <li className={`page-item ${pageSafe === 1 ? 'disabled' : ''}`}>
                <button className='page-link' onClick={() => setCurrentPage(1)}>«</button>
              </li>
              <li className={`page-item ${pageSafe === 1 ? 'disabled' : ''}`}>
                <button className='page-link' onClick={() => setCurrentPage(p => Math.max(1, p - 1))}>Prev</button>
              </li>
              <li className='page-item active'>
                <span className='page-link'>{pageSafe} / {totalPages}</span>
              </li>
              <li className={`page-item ${pageSafe === totalPages ? 'disabled' : ''}`}>
                <button className='page-link' onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}>Next</button>
              </li>
              <li className={`page-item ${pageSafe === totalPages ? 'disabled' : ''}`}>
                <button className='page-link' onClick={() => setCurrentPage(totalPages)}>»</button>
              </li>
            </ul>
          </div>
        </KTCardBody>
      </KTCard>


      {/* Edit Subcategory Modal */}
      {showEditModal && selectedSubcategory && (
        <EditSubcategory
          subcategory={selectedSubcategory}
          categories={categories}
          onClose={() => {
            setShowEditModal(false)
            setSelectedSubcategory(null)
          }}
          onSave={handleEditSubcategory}
        />
      )}

      {/* Delete Confirmation Modal */}
      <Modal
        className='modal fade'
        id='kt_modal_delete_subcategory'
        tabIndex={-1}
        role='dialog'
        show={showDeleteModal}
        onHide={() => {
          setShowDeleteModal(false)
          setSubcategoryToDelete(null)
        }}
      >
        <div className='modal-dialog modal-dialog-centered mw-650px'>
          <div className='modal-content'>
            <div className='modal-header pb-0 border-0 justify-content-end'>
              <div
                className='btn btn-sm btn-icon btn-active-color-primary'
                onClick={() => {
                  setShowDeleteModal(false)
                  setSubcategoryToDelete(null)
                }}
              >
                <KTIcon iconName='cross' className='fs-1' />
              </div>
            </div>
            <div className='modal-body scroll-y mx-5 mx-xl-18 pt-0 pb-15'>
              <div className='mb-13 text-center'>
                <KTIcon iconName='question' className='fs-5tx text-danger mb-5' />
                <h1 className='mb-3'>Delete Subcategory</h1>
                <div className='text-muted fw-semibold fs-5'>
                  Are you sure you want to delete this subcategory?
                  {subcategoryToDelete && (
                    <div className='mt-3'>
                      <strong className='text-dark'>"{subcategoryToDelete.subcategory_name}"</strong>
                    </div>
                  )}
                  <br />This action cannot be undone.
                </div>
              </div>
              <div className='d-flex flex-stack'>
                <button
                  className='btn btn-primary'
                  onClick={() => {
                    setShowDeleteModal(false)
                    setSubcategoryToDelete(null)
                  }}
                >
                  Cancel
                </button>
                <button className='btn btn-danger' onClick={handleDeleteConfirm}>
                  Delete Subcategory
                </button>
              </div>
            </div>
          </div>
        </div>
      </Modal>
    </>
  )
}

export { SubcategoriesList }

